import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import { useProps } from '@tamagui/core';
import type { WithMediaProps } from '@tamagui/core';
import LottieView from 'lottie-react-native';

import SpinnerWithoutLogo from '../../assets/animations/SpinnerNoLogo.json';
import SpinnerWithLogo from '../../assets/animations/SpinnerWithLogo.json';
import { Box } from '../Box';
import type { BoxProps } from '../Box';

const { loaders: loaderTokens } = tokens;

export type LoaderSize = keyof typeof loaderTokens.size.spinnerNoLogo.width;

export type LoaderProps = {
  /**
   * Size of the Loader
   */
  size: LoaderSize;
  /**
   * Determines if Opendoor logo should be shown in the middle of the spinner
   */
  withLogo?: boolean;
  /**
   * Optional Background Color for the Loader
   */
  backgroundColor?: BoxProps['backgroundColor'];
  /**
   * Optional callback for Animation Failing to render
   */
  onAnimationFailure?: (e: string) => void;
} & WithMediaProps<{
  /**
   * This adds size of the accordion
   */
  size: LoaderSize;
}>;

/**
 * Loader Component
 *
 * @example
 * //Usage example:
 *
 * <Loader size="small" withLogo backgroundColor="black"/>
 *
 *
 * @param {LoaderProps} props - The props for the component.
 * @returns {React.JSX.Element} The rendered component.
 */
export const Loader = ({
  backgroundColor,
  withLogo,
  onAnimationFailure,
  ...propsIn
}: LoaderProps) => {
  const { size = 'small' } = useProps(propsIn);
  const loaderType = withLogo ? 'spinnerWithLogo' : 'spinnerNoLogo';

  const width = loaderTokens.size[loaderType].width[size],
    height = loaderTokens.size[loaderType].height[size],
    bgColor = backgroundColor || loaderTokens.color.background.spinnerWithLogo;

  return (
    <Box width={width} height={height} backgroundColor={bgColor}>
      <LottieView
        style={{ width: '100%', height: '100%' }}
        source={withLogo ? SpinnerWithLogo : SpinnerWithoutLogo}
        autoPlay
        loop
        onAnimationFailure={onAnimationFailure}
      />
    </Box>
  );
};
